import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { X, Send, CreditCard, User, AlertCircle } from 'lucide-react';

const CreditTransferModal = ({ onClose, onTransferComplete, users, adminCredits }) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [amount, setAmount] = useState(1);
  const [loading, setLoading] = useState(false);

  const eligibleUsers = users.filter(u => u.user.role !== 'admin' || u.user.id !== selectedUserId);

  const handleTransfer = async (e) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }
    
    if (amount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    
    if (amount > adminCredits) {
      toast.error(`You only have ${adminCredits} credits available`);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/transfer-credits`, {
        recipient_user_id: selectedUserId,
        amount: amount
      });
      
      toast.success(response.data.message);
      onTransferComplete(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer credits');
    } finally {
      setLoading(false);
    }
  };

  const selectedUser = users.find(u => u.user.id === selectedUserId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
          data-testid="close-transfer-modal"
        >
          <X className="w-6 h-6" />
        </button>

        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Send className="w-6 h-6 text-indigo-600" />
            Transfer Credits
          </CardTitle>
          <CardDescription>
            Send credits from your account to a team member
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* Admin's Current Balance */}
          <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-indigo-600" />
                <span className="font-medium text-indigo-900">Your Available Credits</span>
              </div>
              <span className="text-2xl font-bold text-indigo-600" data-testid="admin-credit-balance">
                {adminCredits}
              </span>
            </div>
          </div>

          <form onSubmit={handleTransfer} className="space-y-6">
            {/* Select User */}
            <div>
              <Label htmlFor="user-select" className="text-gray-700">Select Recipient</Label>
              <select
                id="user-select"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="transfer-user-select"
                required
              >
                <option value="">Choose a team member...</option>
                {eligibleUsers.map((profile) => (
                  <option key={profile.user.id} value={profile.user.id}>
                    {profile.user.name} ({profile.user.email}) - {profile.user.credits || 0} credits
                  </option>
                ))}
              </select>
            </div>

            {/* Selected User Preview */}
            {selectedUser && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{selectedUser.user.name}</p>
                    <p className="text-sm text-gray-500">Current balance: {selectedUser.user.credits || 0} credits</p>
                  </div>
                </div>
              </div>
            )}

            {/* Amount */}
            <div>
              <Label htmlFor="amount" className="text-gray-700">Number of Credits to Transfer</Label>
              <Input
                id="amount"
                type="number"
                min="1"
                max={adminCredits}
                value={amount}
                onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
                className="mt-1"
                data-testid="transfer-amount"
                required
              />
              {amount > adminCredits && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Exceeds your available balance
                </p>
              )}
            </div>

            {/* Transfer Summary */}
            {selectedUser && amount > 0 && amount <= adminCredits && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <strong>Transfer Summary:</strong> You will send <strong>{amount}</strong> credit{amount !== 1 ? 's' : ''} to <strong>{selectedUser.user.name}</strong>.
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Your remaining balance: <strong>{adminCredits - amount}</strong> credits
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={loading || !selectedUserId || amount <= 0 || amount > adminCredits}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirm-transfer-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Transferring...' : 'Transfer Credits'}
              </Button>
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreditTransferModal;
